/**
 * 拣货域 Mock 数据和函数
 */

import type { ApiResponse, PageData } from '../../types/common';
import type {
  PickingTaskDTO,
  PickingTaskDetailDTO,
  PickingResultItemDTO,
  QueryPickingTaskPageRequest,
  QueryPlanDetailsRequest,
  QueryPickingResultsRequest,
  AssignPickingTaskRequest,
} from '../../types/picking/pickingTask';
import {
  PickingTaskStatus,
  TaskSource,
  PickingDetailStatus,
  ShortageReason,
} from '../../constants/picking';

/**
 * Mock 工具函数 - 成功响应
 */
function mockSuccess<T>(data: T): ApiResponse<T> {
  return {
    success: true,
    errorMessage: null,
    flag: 'success',
    data,
    traceId: `mock-${Date.now()}`,
  };
}

/**
 * Mock 拣选任务数据
 */
const mockPickingTasks: PickingTaskDTO[] = [
  {
    id: 1,
    code: 'PT20260301001',
    taskCode: 'PT20260301001',
    waveNo: 'W20260301001',
    taskSource: TaskSource.WAVE,
    orderCount: 3,
    skuCount: 15,
    itemCount: 42,
    pickedSkuCount: 15,
    pickedItemCount: 40,
    shortageSkuCount: 1,
    shortageItemCount: 2,
    pickerAccount: 'picker001',
    pickerName: 'John Smith',
    taskStatus: PickingTaskStatus.COMPLETED,
    storeCode: 'S001',
    storeName: 'Lotus Central',
    startTime: 1740825600000,
    finishTime: 1740829200000,
    totalDuration: '01:00:00',
    creatorAccount: 'system',
    creatorName: 'System',
    gmtCreate: 1740825000000,
    modifierAccount: 'system',
    modifierName: 'System',
    gmtModified: 1740829200000,
  },
  {
    id: 2,
    code: 'PT20260301002',
    taskCode: 'PT20260301002',
    waveNo: null,
    taskSource: TaskSource.MANUAL,
    orderCount: 1,
    skuCount: 5,
    itemCount: 12,
    pickedSkuCount: 0,
    pickedItemCount: 0,
    shortageSkuCount: 0,
    shortageItemCount: 0,
    pickerAccount: null,
    pickerName: null,
    taskStatus: PickingTaskStatus.NOT_STARTED,
    storeCode: 'S001',
    storeName: 'Lotus Central',
    startTime: null,
    finishTime: null,
    totalDuration: null,
    creatorAccount: 'system',
    creatorName: 'System',
    gmtCreate: 1740826000000,
    modifierAccount: 'system',
    modifierName: 'System',
    gmtModified: 1740826000000,
  },
  {
    id: 3,
    code: 'PT20260301003',
    taskCode: 'PT20260301003',
    waveNo: 'W20260301002',
    taskSource: TaskSource.WAVE,
    orderCount: 2,
    skuCount: 8,
    itemCount: 20,
    pickedSkuCount: 4,
    pickedItemCount: 10,
    shortageSkuCount: 0,
    shortageItemCount: 0,
    pickerAccount: 'picker002',
    pickerName: 'Jane Doe',
    taskStatus: PickingTaskStatus.IN_PROGRESS,
    storeCode: 'S001',
    storeName: 'Lotus Central',
    startTime: 1740828000000,
    finishTime: null,
    totalDuration: null,
    creatorAccount: 'system',
    creatorName: 'System',
    gmtCreate: 1740827000000,
    modifierAccount: 'system',
    modifierName: 'System',
    gmtModified: 1740828000000,
  },
];

/**
 * Mock 拣选明细数据（Plan Details 用）
 */
const mockPlanDetails: PickingTaskDetailDTO[] = [
  {
    detailId: 'D001',
    taskCode: 'PT20260301001',
    orderNo: 'ORD20260301001',
    sku: 'SKU001',
    barcode: '8850999000001',
    itemName: 'Fresh Milk 1L',
    productImage: null,
    planQuantity: 3,
    actualQuantity: 3,
    locationCode: 'A01-01',
    detailStatus: PickingDetailStatus.PICKED,
    isWeighted: false,
    isFreebie: false,
    isPromotion: false,
    promotionType: null,
  },
  {
    detailId: 'D002',
    taskCode: 'PT20260301001',
    orderNo: 'ORD20260301001',
    sku: 'SKU002',
    barcode: '8850999000002',
    itemName: 'Chicken Breast 500g',
    productImage: null,
    planQuantity: 0.5,
    actualQuantity: 0.48,
    locationCode: 'A02-03',
    detailStatus: PickingDetailStatus.PICKED,
    isWeighted: true,
    isFreebie: false,
    isPromotion: true,
    promotionType: 'B1G1',
  },
  {
    detailId: 'D003',
    taskCode: 'PT20260301001',
    orderNo: 'ORD20260301002',
    sku: 'SKU003',
    barcode: '8850999000003',
    itemName: 'Coca Cola 330ml',
    productImage: null,
    planQuantity: 6,
    actualQuantity: 0,
    locationCode: 'B01-02',
    detailStatus: PickingDetailStatus.SHORTAGE,
    isWeighted: false,
    isFreebie: false,
    isPromotion: true,
    promotionType: 'B2G1',
  },
];

/**
 * Mock 拣货结果数据（Picking Results 用）
 */
const mockPickingResults: PickingResultItemDTO[] = [
  {
    detailId: 1,
    orderNo: 'ORD20260301001',
    sku: 'SKU001',
    itemName: 'Fresh Milk 1L',
    productImage: null,
    planQuantity: 3,
    actualQuantity: 3,
    detailStatus: 'PICKED',
    isWeighted: false,
    isFreebie: false,
    isPromotion: false,
    promotionType: null,
    isSubstituted: false,
    isRepick: false,
    isCancel: false,
    isShortage: false,
    shortageReason: null,
    cancelReason: null,
    pickerName: 'John Smith',
    pickingTime: 1740826000000,
    relatedDetailId: null,
    substituteItems: null,
  },
  {
    detailId: 2,
    orderNo: 'ORD20260301001',
    sku: 'SKU002',
    itemName: 'Chicken Breast 500g',
    productImage: null,
    planQuantity: 0.5,
    actualQuantity: 0.48,
    detailStatus: 'PICKED',
    isWeighted: true,
    isFreebie: false,
    isPromotion: true,
    promotionType: 'B1G1',
    isSubstituted: false,
    isRepick: false,
    isCancel: false,
    isShortage: false,
    shortageReason: null,
    cancelReason: null,
    pickerName: 'John Smith',
    pickingTime: 1740826500000,
    relatedDetailId: null,
    substituteItems: null,
  },
  {
    detailId: 3,
    orderNo: 'ORD20260301002',
    sku: 'SKU003',
    itemName: 'Coca Cola 330ml',
    productImage: null,
    planQuantity: 6,
    actualQuantity: 0,
    detailStatus: 'SHORTAGE',
    isWeighted: false,
    isFreebie: false,
    isPromotion: true,
    promotionType: 'B2G1',
    isSubstituted: false,
    isRepick: false,
    isCancel: false,
    isShortage: true,
    shortageReason: ShortageReason.OUT_OF_STOCK,
    cancelReason: null,
    pickerName: 'John Smith',
    pickingTime: 1740827000000,
    relatedDetailId: null,
    substituteItems: null,
  },
  {
    detailId: 5,
    orderNo: 'ORD20260301003',
    sku: 'SKU006',
    itemName: 'Banana Premium 1kg',
    productImage: null,
    planQuantity: 1.0,
    actualQuantity: 1.2,
    detailStatus: 'REPLACED',
    isWeighted: true,
    isFreebie: false,
    isPromotion: false,
    promotionType: null,
    isSubstituted: true,
    isRepick: false,
    isCancel: false,
    isShortage: false,
    shortageReason: null,
    cancelReason: null,
    pickerName: 'John Smith',
    pickingTime: 1740828000000,
    relatedDetailId: 3,
    substituteItems: null,
  },
  {
    detailId: 6,
    orderNo: 'ORD20260301003',
    sku: 'SKU007',
    itemName: 'Orange Juice 1L (Canceled)',
    productImage: null,
    planQuantity: 2,
    actualQuantity: 0,
    detailStatus: 'CANCELLED',
    isWeighted: false,
    isFreebie: false,
    isPromotion: true,
    promotionType: 'B3G1',
    isSubstituted: false,
    isRepick: false,
    isCancel: true,
    isShortage: false,
    shortageReason: null,
    cancelReason: 'Customer canceled this item',
    pickerName: 'John Smith',
    pickingTime: 1740828500000,
    relatedDetailId: null,
    substituteItems: null,
  },
  {
    detailId: 7,
    orderNo: 'ORD20260301003',
    sku: 'SKU008',
    itemName: 'Pork Belly 500g (Repicked)',
    productImage: null,
    planQuantity: 1.0,
    actualQuantity: 0.95,
    detailStatus: 'REPICKED',
    isWeighted: true,
    isFreebie: false,
    isPromotion: false,
    promotionType: null,
    isSubstituted: false,
    isRepick: true,
    isCancel: false,
    isShortage: false,
    shortageReason: null,
    cancelReason: null,
    pickerName: 'John Smith',
    pickingTime: 1740829000000,
    relatedDetailId: null,
    substituteItems: null,
  },
];

/**
 * 分页查询拣选任务 Mock
 */
export function mockPagePickingTask(
  params: QueryPickingTaskPageRequest
): Promise<ApiResponse<PageData<PickingTaskDTO>>> {
  let filtered = [...mockPickingTasks];
  if (params.taskCode) {
    filtered = filtered.filter(t => t.taskCode.includes(params.taskCode!));
  }
  if (params.waveNo) {
    filtered = filtered.filter(t => t.waveNo?.includes(params.waveNo!) ?? false);
  }
  if (params.taskSource) {
    filtered = filtered.filter(t => t.taskSource === params.taskSource);
  }
  if (params.taskStatus) {
    filtered = filtered.filter(t => t.taskStatus === params.taskStatus);
  }
  const { currentPage = 1, pageSize = 20 } = params;
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  return Promise.resolve(mockSuccess({
    tableData: filtered.slice(start, end),
    paging: { currentPage, pageSize, totalCount: filtered.length },
  }));
}

/**
 * 查询拣选任务详情 Mock
 */
export function mockGetPickingTaskByCode(
  taskCode: string
): Promise<ApiResponse<PickingTaskDTO>> {
  const task = mockPickingTasks.find(t => t.taskCode === taskCode);
  if (task) {
    return Promise.resolve(mockSuccess(task));
  }
  return Promise.resolve({
    success: false,
    errorMessage: 'Task not found',
    flag: 'fail',
    data: null,
    traceId: `mock-${Date.now()}`,
  });
}

/**
 * 查询计划明细 Mock
 */
export function mockPagePlanDetails(
  taskCode: string,
  params: QueryPlanDetailsRequest
): Promise<ApiResponse<PageData<PickingTaskDetailDTO>>> {
  let filtered = mockPlanDetails.filter(d => d.taskCode === taskCode);
  if (params.orderNo) {
    filtered = filtered.filter(d => d.orderNo.includes(params.orderNo!));
  }
  if (params.sku) {
    filtered = filtered.filter(d => d.sku.includes(params.sku!));
  }
  const { currentPage = 1, pageSize = 20 } = params;
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  return Promise.resolve(mockSuccess({
    tableData: filtered.slice(start, end),
    paging: { currentPage, pageSize, totalCount: filtered.length },
  }));
}

/**
 * 查询拣货结果 Mock
 */
export function mockPagePickingResults(
  taskCode: string,
  params: QueryPickingResultsRequest
): Promise<ApiResponse<PageData<PickingResultItemDTO>>> {
  let filtered = [...mockPickingResults];
  if (params.orderNo) {
    filtered = filtered.filter(d => d.orderNo.includes(params.orderNo!));
  }
  if (params.sku) {
    filtered = filtered.filter(d => d.sku.includes(params.sku!));
  }
  if (params.detailStatus) {
    filtered = filtered.filter(d => d.detailStatus === params.detailStatus);
  }
  const { currentPage = 1, pageSize = 20 } = params;
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  return Promise.resolve(mockSuccess({
    tableData: filtered.slice(start, end),
    paging: { currentPage, pageSize, totalCount: filtered.length },
  }));
}

/**
 * 指派拣选任务 Mock
 */
export function mockAssignPickingTask(
  data: AssignPickingTaskRequest
): Promise<ApiResponse<null>> {
  data.taskCodes.forEach(code => {
    const task = mockPickingTasks.find(t => t.taskCode === code);
    if (task) {
      (task as any).pickerAccount = data.pickerAccount;
      (task as any).pickerName = data.pickerName;
    }
  });
  return Promise.resolve(mockSuccess(null));
}

/**
 * 导出拣选任务 Mock
 */
export function mockExportPickingTask(
  _params: QueryPickingTaskPageRequest
): Promise<ApiResponse<{ downloadUrl: string; fileName: string }>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(mockSuccess({
        downloadUrl: 'https://example.com/mock-picking-task-export.xlsx',
        fileName: 'picking_task_export_mock.xlsx',
      }));
    }, 500);
  });
}
